import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../utils/api';
import WeekDatumPicker from '../components/WeekDatumPicker';
import { weekMetDatum, volledigeDatum } from '../utils/datum';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 700);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 700);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isMobile;
}

const STATUS_CHIP = {
  gepland:       { bg:'#F0F0F0', cl:'#555',    label:'Gepland' },
  in_uitvoering: { bg:'#E6F1FB', cl:'#0C447C', label:'In uitvoering' },
  afgewerkt:     { bg:'#EAF3DE', cl:'#27500A', label:'Afgewerkt' },
  voltooid:      { bg:'#EAF3DE', cl:'#27500A', label:'Voltooid' },
  on_hold:       { bg:'#FDF3E3', cl:'#854F0B', label:'On hold' },
  geannuleerd:   { bg:'#FDEDEC', cl:'#A32D2D', label:'Geannuleerd' },
};

const PRIO_CHIP = {
  normaal: { bg:'#F0F0F0', cl:'#555',    label:'Normaal' },
  hoog:    { bg:'#FDF3E3', cl:'#854F0B', label:'Hoog' },
  spoed:   { bg:'#FDEDEC', cl:'#A32D2D', label:'Spoed' },
};

const STAP_BG    = { wachtend:'#F5F5F5', actief:'#EBF3FF', voltooid:'#EAF3DE', overgeslagen:'#FAFAFA' };
const STAP_CL    = { wachtend:'#888', actief:'#0C447C', voltooid:'#27500A', overgeslagen:'#BBB' };
const STAP_LABEL = { wachtend:'Wachtend', actief:'Actief', voltooid:'Voltooid', overgeslagen:'Overgeslagen' };

const MAT_STATUS_LABEL = { te_bestellen:'Te bestellen', besteld:'Besteld', geleverd:'Geleverd', in_stock:'In stock' };
const MAT_STATUS_KL    = { te_bestellen:'#C0392B', besteld:'#B5660E', geleverd:'#185FA5', in_stock:'#1A7A3C' };
const MAT_STATUS_BG    = { te_bestellen:'#FDEDEC', besteld:'#FDF3E3', geleverd:'#E6F1FB', in_stock:'#EAF3DE' };

const ACTIE_CFG = {
  ORDER_AANGEMAAKT:          { icon: '🆕', kleur: '#1A7A3C', label: 'Order aangemaakt' },
  ORDER_BIJGEWERKT:          { icon: '✏️', kleur: '#185FA5', label: 'Order bijgewerkt' },
  STAP_VOLTOOID:             { icon: '✅', kleur: '#1A7A3C', label: 'Stap voltooid' },
  STAP_TERUGGEZET:           { icon: '↩️', kleur: '#B5660E', label: 'Stap teruggezet' },
  STAP_TOEGEVOEGD:           { icon: '➕', kleur: '#185FA5', label: 'Stap toegevoegd' },
  STAP_VERWIJDERD:           { icon: '🗑️', kleur: '#A32D2D', label: 'Stap verwijderd' },
  INSTRUCTIES_GEWIJZIGD:     { icon: '📋', kleur: '#267A5C', label: 'Instructies gewijzigd' },
  MATERIAAL_TOEGEVOEGD:      { icon: '📦', kleur: '#185FA5', label: 'Materiaal toegevoegd' },
  MATERIAAL_BIJGEWERKT:      { icon: '📦', kleur: '#267A5C', label: 'Materiaal bijgewerkt' },
  MATERIAAL_VERWIJDERD:      { icon: '📦', kleur: '#A32D2D', label: 'Materiaal verwijderd' },
  MATERIAALSTATUS_GEWIJZIGD: { icon: '🔄', kleur: '#854F0B', label: 'Materiaalstatus gewijzigd' },
  MATERIAAL_STATUS_GEWIJZIGD:{ icon: '🔄', kleur: '#854F0B', label: 'Materiaalstatus gewijzigd' },
  STATUS_GEWIJZIGD:          { icon: '🔀', kleur: '#534AB7', label: 'Orderstatus gewijzigd' },
  ORDER_GEDUPLICEERD:        { icon: '📋', kleur: '#534AB7', label: 'Order gedupliceerd' },
  LEVERDATUM_GEWIJZIGD:      { icon: '📅', kleur: '#854F0B', label: 'Leverdatum gewijzigd' },
  MATERIAALLEVERDATUM_GEWIJZIGD: { icon: '📅', kleur: '#854F0B', label: 'Materiaalleverdatum gewijzigd' },
  MATERIAAL_GEBRUIK_INGEGEVEN:   { icon: '📦', kleur: '#267A5C', label: 'Materiaalgebruik geregistreerd' },
};

const STATUS_NL = {
  gepland: 'Gepland', in_uitvoering: 'In uitvoering', afgewerkt: 'Afgewerkt',
  voltooid: 'Voltooid', on_hold: 'On hold', geannuleerd: 'Geannuleerd',
};
const MAT_NL2 = { te_bestellen: 'Te bestellen', besteld: 'Besteld', geleverd: 'Geleverd', in_stock: 'In stock' };
const PRIO_NL = { normaal: 'Normaal', hoog: 'Hoog', spoed: 'Spoed' };

const S = {
  input:    { padding: '6px 10px', border: '1px solid #DDD', borderRadius: 6, fontSize: 13, width: '100%', boxSizing: 'border-box' },
  btnGhost: { padding: '5px 12px', background: 'none', border: '1px solid #DDD', borderRadius: 6, fontSize: 12, cursor: 'pointer', color: '#555' },
  btnGreen: { padding: '6px 14px', background: '#1A7A3C', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer' },
  btnBlue:  { padding: '6px 14px', background: '#185FA5', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer' },
  btnRed:   { padding: '5px 12px', background: 'none', border: '1px solid #E05040', borderRadius: 6, fontSize: 12, cursor: 'pointer', color: '#C0392B' },
  kaart:    { background: '#fff', border: '1px solid #E0E0E0', borderRadius: 10, padding: 20, marginBottom: 16 },
};

const leegMateriaal = { naam:'', beschrijving:'', referentie:'', leverancier_id:'', eenheid:'stuks', tanden_per_stuk:'', vereist_voor_operaties:[], status:'te_bestellen', besteldatum:'', leverdatum_verwacht:'', opmerkingen:'' };

function datFmt(d) {
  if (!d) return '—';
  return weekMetDatum(d);
}

function Chip({ bg, cl, children }) {
  return <span style={{ fontSize: 11, fontWeight: 600, background: bg, color: cl, borderRadius: 99, padding: '2px 9px', display: 'inline-block' }}>{children}</span>;
}

function InfoVak({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, color: '#1F3864' }}>{children || '—'}</div>
    </div>
  );
}

// ─── MATERIAAL FORM ───────────────────────────────────────────────────────────

function MateriaalForm({ form, setForm, routeOperaties, leveranciers, onSla, onAnnuleer, bezig, orderAantal }) {
  const isTanden = form.tanden_per_stuk !== '' && form.tanden_per_stuk !== null;
  const totaalTanden = isTanden && form.tanden_per_stuk && orderAantal
    ? parseInt(form.tanden_per_stuk) * orderAantal : null;

  const toggleOp = (nr) => setForm(f => ({
    ...f,
    vereist_voor_operaties: f.vereist_voor_operaties.includes(nr)
      ? f.vereist_voor_operaties.filter(n => n !== nr)
      : [...f.vereist_voor_operaties, nr],
  }));

  return (
    <div style={{ background: '#F7F9FC', border: '1px solid #C5D8F0', borderRadius: 8, padding: 16, marginTop: 8 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, marginBottom: 10 }}>
        <div>
          <label style={{ fontSize: 11, color: '#666' }}>Naam *</label>
          <input style={S.input} value={form.naam} onChange={e => setForm(f => ({ ...f, naam: e.target.value }))} placeholder="bijv. Staal, Tanden HM" />
        </div>
        <div>
          <label style={{ fontSize: 11, color: '#666' }}>Referentie</label>
          <input style={S.input} value={form.referentie} onChange={e => setForm(f => ({ ...f, referentie: e.target.value }))} placeholder="Artikelnummer leverancier" />
        </div>
        <div>
          <label style={{ fontSize: 11, color: '#666' }}>Leverancier</label>
          <select style={S.input} value={form.leverancier_id} onChange={e => setForm(f => ({ ...f, leverancier_id: e.target.value }))}>
            <option value="">— geen —</option>
            {leveranciers.map(l => <option key={l.id} value={l.id}>{l.naam}</option>)}
          </select>
        </div>
      </div>

      <div style={{ background: '#FFFBF0', border: '1px solid #F0C060', borderRadius: 6, padding: '10px 14px', marginBottom: 10 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 500, color: '#8A5C00', marginBottom: isTanden ? 8 : 0, cursor: 'pointer' }}>
          <input type="checkbox" checked={isTanden}
            onChange={e => setForm(f => ({ ...f, tanden_per_stuk: e.target.checked ? '1' : '', eenheid: e.target.checked ? 'tanden' : 'stuks' }))} />
          Tanden-berekening (HM-tanden, zaagbladinzet...)
        </label>
        {isTanden && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <input type="number" min="1" style={{ ...S.input, width: 100 }} placeholder="Tanden/stuk"
              value={form.tanden_per_stuk}
              onChange={e => setForm(f => ({ ...f, tanden_per_stuk: e.target.value, eenheid: 'tanden' }))} />
            <span style={{ fontSize: 13, color: '#888' }}>× {orderAantal} stuks =</span>
            <span style={{ fontSize: 16, fontWeight: 700, color: '#8A5C00' }}>
              {totaalTanden ? `${totaalTanden} tanden` : '?'}
            </span>
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10, marginBottom: 10 }}>
        <div>
          <label style={{ fontSize: 11, color: '#666' }}>Eenheid</label>
          <input style={S.input} value={form.eenheid} onChange={e => setForm(f => ({ ...f, eenheid: e.target.value }))} placeholder="stuks, kg, m..." />
        </div>
        <div>
          <label style={{ fontSize: 11, color: '#666' }}>Status</label>
          <select style={S.input} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
            {Object.entries(MAT_STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <WeekDatumPicker
          label="Besteldatum"
          value={form.besteldatum}
          onChange={v => setForm(f => ({ ...f, besteldatum: v }))}
        />
        <WeekDatumPicker
          label="Verwachte levering"
          value={form.leverdatum_verwacht}
          onChange={v => setForm(f => ({ ...f, leverdatum_verwacht: v }))}
          eindDatum
        />
      </div>

      {routeOperaties.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 11, color: '#666', display: 'block', marginBottom: 4 }}>Vereist voor stap (blokkeert activering)</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {routeOperaties.map(op => {
              const sel = form.vereist_voor_operaties.includes(op.operatie_nummer);
              return (
                <button key={op.operatie_nummer} type="button" onClick={() => toggleOp(op.operatie_nummer)} style={{
                  padding: '4px 10px', borderRadius: 99, fontSize: 12, cursor: 'pointer',
                  border: sel ? '1.5px solid #185FA5' : '1px solid #DDD',
                  background: sel ? '#E6F1FB' : '#fff', color: sel ? '#0C447C' : '#555', fontWeight: sel ? 600 : 400,
                }}>{op.label || op.operatie_naam}</button>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ marginBottom: 10 }}>
        <label style={{ fontSize: 11, color: '#666' }}>Opmerkingen</label>
        <input style={S.input} value={form.opmerkingen} onChange={e => setForm(f => ({ ...f, opmerkingen: e.target.value }))} placeholder="Optioneel" />
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button style={S.btnGreen} onClick={onSla} disabled={bezig || !form.naam}>Opslaan</button>
        <button style={S.btnGhost} onClick={onAnnuleer}>Annuleren</button>
      </div>
    </div>
  );
}

// ─── MATERIAAL RIJ ────────────────────────────────────────────────────────────

function MateriaalRij({ mat, routeOperaties, leveranciers, orderAantal, isPlanner, onVervers }) {
  const [bewerkForm, setBewerkForm] = useState(null);
  const [bezig, setBezig] = useState(false);
  const [statusBezig, setStatusBezig] = useState(false);

  const startBewerk = () => setBewerkForm({
    naam: mat.naam || '', beschrijving: mat.beschrijving || '',
    referentie: mat.referentie || '', leverancier_id: mat.leverancier_id || '',
    eenheid: mat.eenheid || 'stuks', tanden_per_stuk: mat.tanden_per_stuk || '',
    vereist_voor_operaties: mat.vereist_voor_operaties || [], status: mat.status,
    besteldatum: mat.besteldatum ? mat.besteldatum.substring(0, 10) : '',
    leverdatum_verwacht: mat.leverdatum_verwacht ? mat.leverdatum_verwacht.substring(0, 10) : '',
    opmerkingen: mat.opmerkingen || '',
  });

  const slaOp = async () => {
    setBezig(true);
    try {
      await api.put(`/orders/${mat.order_id}/materialen/${mat.id}`, {
        ...bewerkForm,
        tanden_per_stuk: bewerkForm.tanden_per_stuk ? parseInt(bewerkForm.tanden_per_stuk) : null,
        leverancier_id: bewerkForm.leverancier_id || null,
      });
      setBewerkForm(null); onVervers();
    } catch (e) { alert('Fout: ' + (e.response?.data?.error || e.message)); }
    setBezig(false);
  };

  const wijzigStatus = async (nieuweStatus) => {
    setStatusBezig(true);
    try {
      const r = await api.patch(`/orders/${mat.order_id}/materialen/${mat.id}/status`, { status: nieuweStatus });
      if (r.data.geblokkeerde_stappen > 0)
        alert(`Let op: ${r.data.geblokkeerde_stappen} stap(pen) teruggezet naar 'Wachtend' omdat dit materiaal niet beschikbaar is.`);
      onVervers();
    } catch (e) { alert('Fout: ' + (e.response?.data?.error || e.message)); }
    setStatusBezig(false);
  };

  const verwijder = async () => {
    if (!window.confirm(`Materiaal "${mat.naam}" verwijderen?`)) return;
    await api.delete(`/orders/${mat.order_id}/materialen/${mat.id}`);
    onVervers();
  };

  const kl = MAT_STATUS_KL[mat.status];
  const bg = MAT_STATUS_BG[mat.status];
  const actiefDatum = mat.leverdatum_verwacht_herzien || mat.leverdatum_verwacht;
  const isOverdue = actiefDatum && new Date(actiefDatum) < new Date() && mat.status !== 'geleverd';

  return (
    <div style={{ borderBottom: '1px solid #F0F0F0', padding: '12px 0' }}>
      {bewerkForm ? (
        <MateriaalForm form={bewerkForm} setForm={setBewerkForm}
          routeOperaties={routeOperaties} leveranciers={leveranciers}
          orderAantal={orderAantal} onSla={slaOp}
          onAnnuleer={() => setBewerkForm(null)} bezig={bezig} />
      ) : (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
              <span style={{ fontWeight: 600, fontSize: 14, color: '#1F3864' }}>{mat.naam}</span>
              {mat.referentie && <span style={{ fontSize: 12, color: '#888' }}>{mat.referentie}</span>}
              {mat.leverancier_naam && <span style={{ fontSize: 12, color: '#555', background: '#F5F5F5', padding: '1px 6px', borderRadius: 4 }}>{mat.leverancier_naam}</span>}
            </div>

            {mat.tanden_per_stuk && (
              <div style={{ fontSize: 12, color: '#8A5C00', background: '#FFFBF0', border: '1px solid #F0C060', borderRadius: 5, padding: '3px 10px', display: 'inline-block', marginBottom: 4 }}>
                {mat.tanden_per_stuk} t/stuk × {orderAantal} stuks = <strong>{mat.totaal_tanden} tanden</strong>
              </div>
            )}

            {mat.vereist_voor_operaties?.length > 0 && (
              <div style={{ fontSize: 11, color: '#555', marginBottom: 4 }}>
                Vereist voor: {routeOperaties.filter(op => mat.vereist_voor_operaties.includes(op.operatie_nummer)).map(op => op.label || op.operatie_naam).join(', ')}
              </div>
            )}

            {actiefDatum && (
              <div style={{ fontSize: 11, color: isOverdue ? '#C0392B' : '#888', marginBottom: 2 }}>
                {isOverdue ? '⚠ ' : ''}Verwachte levering:{' '}
                <strong style={{ color: isOverdue ? '#C0392B' : '#333' }}>{datFmt(actiefDatum)}</strong>
                {mat.leverdatum_verwacht_herzien && (
                  <>
                    <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 99, background: '#FFF2CC', color: '#7F6000', border: '1px solid #F0C060', fontWeight: 600, fontSize: 10 }}>herzien</span>
                    <span style={{ color: '#BBB', marginLeft: 6 }}>orig: {datFmt(mat.leverdatum_verwacht)}</span>
                  </>
                )}
                {mat.besteldatum && <span style={{ color: '#AAA', marginLeft: 6 }}>· besteld {datFmt(mat.besteldatum)}</span>}
              </div>
            )}
            {mat.opmerkingen && <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>{mat.opmerkingen}</div>}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
            {isPlanner ? (
              <select value={mat.status} onChange={e => wijzigStatus(e.target.value)} disabled={statusBezig}
                style={{ fontSize: 12, fontWeight: 600, color: kl, background: bg, border: `1px solid ${kl}40`, borderRadius: 6, padding: '4px 8px', cursor: 'pointer' }}>
                {Object.entries(MAT_STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            ) : (
              <span style={{ fontSize: 12, fontWeight: 600, color: kl, background: bg, border: `1px solid ${kl}40`, borderRadius: 6, padding: '4px 10px' }}>
                {MAT_STATUS_LABEL[mat.status]}
              </span>
            )}
            {isPlanner && (
              <div style={{ display: 'flex', gap: 6 }}>
                <button style={S.btnGhost} onClick={startBewerk}>Bewerk</button>
                <button style={S.btnRed} onClick={verwijder}>Wis</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── HOOFD COMPONENT ──────────────────────────────────────────────────────────

export default function OrderDetailPage({ user }) {
  const { id } = useParams();
  const nav = useNavigate();
  const loc = useLocation();
  const isMobile = useIsMobile();
  const terugPad = loc.state?.from || (user?.rol === 'werkvloer' ? '/productie' : '/orders');
  const [order, setOrder] = useState(null);
  const [laden, setLaden] = useState(true);
  const [stapOpmerking, setStapOpmerking] = useState({});
  const [bezig, setBezig] = useState(null);
  const [materiaalBezig, setMateriaalBezig] = useState(false);
  const [leveranciers, setLeveranciers] = useState([]);
  const [nieuwMateriaal, setNieuwMateriaal] = useState(null);
  const [matBezig, setMatBezig] = useState(false);
  const [bewerkForm, setBewerkForm] = useState(null);
  const [bewerkBezig, setBewerkBezig] = useState(false);
  const [instructiesEdit, setInstructiesEdit] = useState(null); // { stapId, tekst }
  const [tijdEdit, setTijdEdit] = useState({}); // stapId → minuten string
  const [leverdatumEdit, setLeverdatumEdit] = useState(false);
  const [leverdatumNieuw, setLeverdatumNieuw] = useState('');
  const [operaties, setOperaties] = useState([]);
  const [nieuwStap, setNieuwStap] = useState(null); // { operatie_id, label, is_extern, externe_partij }
  const [stapBezig, setStapBezig] = useState(false);
  const [prioEdit, setPrioEdit] = useState(false);
  const [volgordeEdit, setVolgordeEdit] = useState(null); // null = normaal, array = bewerkmodus
  const [volgordeBezig, setVolgordeBezig] = useState(false);

  const isPlanner = ['planner', 'admin'].includes(user?.rol);
  const isBackoffice = user?.rol === 'backoffice';
  const kanPrioriteitWijzigen = isPlanner || isBackoffice;

  const laadOrder = useCallback(() => {
    api.get(`/orders/${id}`).then(r => { setOrder(r.data); setLaden(false); });
  }, [id]);

  useEffect(() => {
    laadOrder();
    api.get('/leveranciers').then(r => setLeveranciers(r.data));
    api.get('/operaties').then(r => setOperaties(r.data));
  }, [laadOrder]);

  const wijzigMateriaalStatus = async (nieuweStatus) => {
    setMateriaalBezig(true);
    try {
      await api.patch(`/orders/${id}/materiaalstatus`, { materiaal_status: nieuweStatus });
      laadOrder();
    } catch (e) { alert('Fout: ' + e.message); }
    setMateriaalBezig(false);
  };

  const slaInstructiesOp = async () => {
    const { stapId, tekst } = instructiesEdit;
    setInstructiesEdit(null);
    try {
      await api.patch(`/orders/${id}/stap/${stapId}/instructies`, { instructies: tekst });
      setOrder(o => ({ ...o, route: o.route.map(s => s.id === stapId ? { ...s, instructies: tekst || null } : s) }));
    } catch (e) { alert('Fout: ' + (e.response?.data?.error || e.message)); }
  };

  const slaLeverdatumOp = async () => {
    if (!leverdatumNieuw) return;
    try {
      await api.patch(`/orders/${id}/leverdatum`, { leverdatum: leverdatumNieuw });
      setOrder(o => ({
        ...o,
        leverdatum_origineel: o.leverdatum_origineel || o.leverdatum,
        leverdatum: leverdatumNieuw,
      }));
      setLeverdatumEdit(false);
      setLeverdatumNieuw('');
      laadOrder();
    } catch (e) { alert('Fout: ' + (e.response?.data?.error || e.message)); }
  };

  const slaaTijdOp = async (stapId) => {
    const min = parseFloat(tijdEdit[stapId]);
    try {
      await api.patch(`/orders/${id}/stap/${stapId}/tijd`, { tijd_min: isNaN(min) ? null : min });
      setOrder(o => ({ ...o, route: o.route.map(s => s.id === stapId ? { ...s, tijd_min: isNaN(min) ? null : min } : s) }));
      setTijdEdit(t => { const n = { ...t }; delete n[stapId]; return n; });
    } catch (e) { alert('Fout: ' + (e.response?.data?.error || e.message)); }
  };

  const vinkAf = async (stapId) => {
    setBezig(stapId);
    try {
      await api.patch(`/orders/${id}/stap/${stapId}/voltooid`, { opmerking: stapOpmerking[stapId] || '' });
      laadOrder();
    } catch (e) { alert('Fout: ' + e.message); }
    setBezig(null);
  };

  const wijzigPrioriteit = async (nieuweWaarde) => {
    try {
      await api.patch(`/orders/${id}/prioriteit`, { prioriteit: nieuweWaarde });
      setOrder(o => ({ ...o, prioriteit: nieuweWaarde }));
    } catch (e) { alert('Fout: ' + (e.response?.data?.error || e.message)); }
  };

  const voegStapToe = async () => {
    if (!nieuwStap?.operatie_id) return;
    setStapBezig(true);
    try {
      const volgorde = (order.route?.length || 0) + 1;
      await api.post(`/orders/${id}/stap`, {
        operatie_id: parseInt(nieuwStap.operatie_id),
        volgorde,
        label: nieuwStap.label || null,
        is_extern: nieuwStap.is_extern || false,
        externe_partij: nieuwStap.externe_partij || null,
      });
      setNieuwStap(null);
      laadOrder();
    } catch (e) { alert('Fout: ' + (e.response?.data?.error || e.message)); }
    setStapBezig(false);
  };

  const verwijderStap = async (stapId, stapNaam) => {
    if (!window.confirm(`Stap "${stapNaam}" verwijderen? Dit kan niet ongedaan gemaakt worden.`)) return;
    try {
      await api.delete(`/orders/${id}/stap/${stapId}`);
      laadOrder();
    } catch (e) { alert('Fout: ' + (e.response?.data?.error || e.message)); }
  };

  const startVolgordeEdit = () => {
    const gesorteerd = [...(order.route || [])].sort((a, b) => a.volgorde - b.volgorde);
    setVolgordeEdit(gesorteerd);
    setNieuwStap(null); // sluit "stap toevoegen" als die open was
  };

  const verschuifStap = (index, richting) => {
    const nieuw = [...volgordeEdit];
    const doel = index + richting;
    if (doel < 0 || doel >= nieuw.length) return;
    [nieuw[index], nieuw[doel]] = [nieuw[doel], nieuw[index]];
    setVolgordeEdit(nieuw);
  };

  const slaVolgordeOp = async () => {
    setVolgordeBezig(true);
    try {
      const stappen = volgordeEdit.map((s, i) => ({ id: s.id, volgorde: i + 1 }));
      await api.patch(`/orders/${id}/route/volgorde`, { stappen });
      setVolgordeEdit(null);
      laadOrder();
    } catch (e) { alert('Fout: ' + (e.response?.data?.error || e.message)); }
    setVolgordeBezig(false);
  };

  const zetTerug = async (stapId) => {
    if (!window.confirm('Stap terugzetten naar actief? Dit kan invloed hebben op de planning.')) return;
    setBezig(stapId);
    try {
      await api.patch(`/orders/${id}/stap/${stapId}/actief`);
      laadOrder();
    } catch (e) { alert('Fout: ' + (e.response?.data?.error || e.message)); }
    setBezig(null);
  };

  const voegMateriaalToe = async () => {
    if (!nieuwMateriaal?.naam) return;
    setMatBezig(true);
    try {
      await api.post(`/orders/${id}/materialen`, {
        ...nieuwMateriaal,
        tanden_per_stuk: nieuwMateriaal.tanden_per_stuk ? parseInt(nieuwMateriaal.tanden_per_stuk) : null,
        leverancier_id: nieuwMateriaal.leverancier_id || null,
      });
      setNieuwMateriaal(null); laadOrder();
    } catch (e) { alert('Fout: ' + (e.response?.data?.error || e.message)); }
    setMatBezig(false);
  };

  const openBewerk = () => setBewerkForm({
    ordernummer: order.ordernummer || '',
    klant: order.klant || '',
    klant_referentie: order.klant_referentie || '',
    artikel_omschrijving: order.artikel_omschrijving || '',
    aantal: order.aantal || 1,
    startdatum: order.startdatum ? order.startdatum.substring(0, 10) : '',
    leverdatum: order.leverdatum ? order.leverdatum.substring(0, 10) : '',
    prioriteit: order.prioriteit || 'normaal',
    status: order.status || 'gepland',
    tekeningnummer: order.tekeningnummer || '',
    materiaal_beschrijving: order.materiaal_beschrijving || '',
    materiaal_referentie: order.materiaal_referentie || '',
    materiaal_leverancier_id: order.materiaal_leverancier_id || '',
    materiaal_status: order.materiaal_status || 'in_stock',
    uitbesteding_info: order.uitbesteding_info || '',
    opmerkingen: order.opmerkingen || '',
    diameter_mm: order.diameter_mm || '',
    tanden_per_stuk: order.tanden_per_stuk || '',
  });

  const slaOrderOp = async () => {
    setBewerkBezig(true);
    try {
      await api.put(`/orders/${id}`, {
        ...bewerkForm,
        aantal: parseInt(bewerkForm.aantal),
        materiaal_leverancier_id: bewerkForm.materiaal_leverancier_id || null,
      });
      setBewerkForm(null); laadOrder();
    } catch (e) { alert('Fout: ' + (e.response?.data?.error || e.message)); }
    setBewerkBezig(false);
  };

  const verwijderOrder = async () => {
    if (!window.confirm(`Order ${order.ordernummer} definitief verwijderen?`)) return;
    try {
      await api.delete(`/orders/${id}`);
      nav('/orders');
    } catch (e) { alert('Fout: ' + (e.response?.data?.error || e.message)); }
  };

  const dupliceerOrder = async () => {
    if (!window.confirm(`Order ${order.ordernummer} dupliceren?`)) return;
    try {
      const r = await api.post(`/orders/${id}/dupliceer`);
      nav(`/orders/${r.data.id}`);
    } catch (e) { alert('Fout: ' + (e.response?.data?.error || e.message)); }
  };

  if (laden) return <div style={{ padding: 40, color: '#888' }}>Laden...</div>;
  if (!order) return <div style={{ padding: 40, color: '#888' }}>Order niet gevonden</div>;

  const totaalStappen = order.route?.length || 0;
  const voltooideStappen = order.route?.filter(s => s.status === 'voltooid').length || 0;
  const voortgang = totaalStappen > 0 ? Math.round((voltooideStappen / totaalStappen) * 100) : 0;

  const dagenResterend = parseInt(order.dagen_resterend);
  const isTeLaat = dagenResterend < 0;
  const isVandaag = dagenResterend === 0;
  const urgentieBg = isTeLaat ? '#FDEDEC' : (dagenResterend <= 3 ? '#FDF3E3' : '#EAF3DE');
  const urgentieCl = isTeLaat ? '#A32D2D' : (dagenResterend <= 3 ? '#854F0B' : '#27500A');
  const urgentieTxt = isTeLaat
    ? `${Math.abs(dagenResterend)}d te laat`
    : isVandaag ? 'Vandaag!' : `${dagenResterend}d`;

  const statusChip = STATUS_CHIP[order.status] || { bg:'#F0F0F0', cl:'#666', label: order.status };
  const prioChip   = PRIO_CHIP[order.prioriteit]  || { bg:'#F0F0F0', cl:'#666', label: order.prioriteit };

  const routeOperaties = order.route?.map(s => ({
    operatie_nummer: s.operatie_nummer,
    operatie_naam: s.operatie_naam,
    label: s.label,
  })) || [];

  const heeftHoofdMateriaal = order.materiaal_beschrijving || order.materiaal_referentie || order.materiaal_leverancier_id;

  const berekenStapTijd = (stap) => {
    if (stap.tijdsdriver === 'order_specifiek' || !stap.tijdsdriver || stap.tijdsdriver === 'vast') {
      const setup = parseFloat(stap.setup_min || 0);
      if (stap.tijdsdriver === 'order_specifiek') return null;
      return setup > 0 ? { op: setup, mac: 0, label: `${setup.toFixed(0)} min setup` } : null;
    }
    const n    = order.aantal || 1;
    const t    = order.tanden_per_stuk || 0;
    const d    = parseFloat(order.diameter_mm || 0);
    const op =
      parseFloat(stap.setup_min || 0) +
      parseFloat(stap.operator_min_per_stuk || 0) * n +
      parseFloat(stap.operator_min_per_tand || 0) * t * n +
      parseFloat(stap.operator_min_per_mm   || 0) * d * n;
    const mac =
      parseFloat(stap.machine_min_per_stuk || 0) * n +
      parseFloat(stap.machine_min_per_tand || 0) * t * n +
      parseFloat(stap.machine_min_per_mm   || 0) * d * n;
    if (op === 0 && mac === 0) return null;
    return { op, mac };
  };

  const fmtMin = (min) => {
    if (min < 60) return `${Math.round(min)} min`;
    const h = Math.floor(min / 60), m = Math.round(min % 60);
    return m > 0 ? `${h}u ${m}min` : `${h}u`;
  };

  return (
    <div style={{ padding: isMobile ? '12px 10px' : 24, maxWidth: 940 }}>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: 16 }}>
        {/* Terug + acties */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
          <button onClick={() => nav(terugPad)} style={{ background: 'none', border: 'none', color: '#185FA5', cursor: 'pointer', fontSize: 13, padding: 0, flexShrink: 0 }}>← Terug</button>
          {order.familie_kleur && <div style={{ width: 9, height: 9, borderRadius: '50%', background: order.familie_kleur, flexShrink: 0 }} />}
          <h1 style={{ margin: 0, fontSize: isMobile ? 17 : 20, fontWeight: 700, color: '#1F3864' }}>{order.ordernummer}</h1>
          {!isMobile && <span style={{ fontSize: 14, color: '#555' }}>{order.klant}</span>}
          {isPlanner && (
            <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
              <button style={S.btnGhost} onClick={openBewerk}>Bewerk</button>
              {!isMobile && <button style={{ ...S.btnGhost, borderColor: '#185FA5', color: '#185FA5' }} onClick={dupliceerOrder}>Dupliceer</button>}
              {!isMobile && <button style={S.btnRed} onClick={verwijderOrder}>Verwijder</button>}
            </div>
          )}
        </div>

        {/* Chips */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
          {isMobile && <span style={{ fontSize: 13, color: '#555', fontWeight: 500 }}>{order.klant}</span>}
          <Chip bg={urgentieBg} cl={urgentieCl}>{urgentieTxt}</Chip>
          <Chip bg={statusChip.bg} cl={statusChip.cl}>{statusChip.label}</Chip>
          {kanPrioriteitWijzigen ? (
            <div style={{ position: 'relative' }}>
              <button onClick={() => setPrioEdit(p => !p)}
                style={{ fontSize: 11, fontWeight: 600, background: prioChip.bg, color: prioChip.cl, borderRadius: 99, padding: '2px 9px', border: `1px solid ${prioChip.cl}40`, cursor: 'pointer' }}
                title="Prioriteit wijzigen">
                {prioChip.label} ▾
              </button>
              {prioEdit && (
                <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 100, background: '#fff', border: '1px solid #DDD', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', padding: 6, marginTop: 4, minWidth: 110 }}>
                  {Object.entries(PRIO_CHIP).map(([k, v]) => (
                    <button key={k} onClick={() => { wijzigPrioriteit(k); setPrioEdit(false); }}
                      style={{ display: 'block', width: '100%', padding: '6px 10px', background: order.prioriteit === k ? v.bg : 'transparent', color: v.cl, border: 'none', borderRadius: 6, fontSize: 12, fontWeight: order.prioriteit === k ? 600 : 400, cursor: 'pointer', textAlign: 'left' }}>
                      {v.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            order.prioriteit !== 'normaal' && <Chip bg={prioChip.bg} cl={prioChip.cl}>{prioChip.label}</Chip>
          )}
          {order.familie_naam && <span style={{ fontSize: 11, color: '#888', background: '#F5F5F5', padding: '2px 8px', borderRadius: 4 }}>{order.familie_naam}</span>}
        </div>

        {/* Voortgangsbalk */}
        {totaalStappen > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ flex: 1, height: 6, background: '#EEE', borderRadius: 99 }}>
              <div style={{ width: `${voortgang}%`, height: 6, background: voortgang === 100 ? '#639922' : '#185FA5', borderRadius: 99, transition: 'width 0.3s' }} />
            </div>
            <span style={{ fontSize: 12, color: '#888', minWidth: 80, textAlign: 'right' }}>
              {voltooideStappen}/{totaalStappen} · {voortgang}%
            </span>
          </div>
        )}
      </div>

      {/* ── Order info ─────────────────────────────────────────────────────── */}
      <div style={S.kaart}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: isMobile ? 10 : 16, marginBottom: 14 }}>
          <InfoVak label="Klant">{order.klant}</InfoVak>
          <InfoVak label="Klant referentie">{order.klant_referentie}</InfoVak>
          <InfoVak label="Aantal">{order.aantal} stuks</InfoVak>
          <div style={{ gridColumn: '1 / -1' }}>
            <InfoVak label="Artikel / omschrijving">{order.artikel_omschrijving}</InfoVak>
          </div>
          <InfoVak label="Startdatum">{datFmt(order.startdatum)}</InfoVak>

          {/* Leverdatum met herzien-tracking */}
          <div>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>Leverdatum</div>
            {leverdatumEdit && isPlanner ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <WeekDatumPicker
                  value={leverdatumNieuw}
                  onChange={setLeverdatumNieuw}
                  eindDatum
                  style={{ minWidth: 220 }}
                />
                <button onClick={slaLeverdatumOp} disabled={!leverdatumNieuw} style={{ ...S.btnGreen, padding: '5px 12px' }}>Opslaan</button>
                <button onClick={() => { setLeverdatumEdit(false); setLeverdatumNieuw(''); }} style={S.btnGhost}>Annuleren</button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, color: '#1F3864', fontWeight: 500 }}>{datFmt(order.leverdatum)}</span>
                {order.leverdatum_origineel && (
                  <>
                    <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 99, background: '#FFF2CC', color: '#7F6000', border: '1px solid #F0C060', fontWeight: 600 }}>herzien</span>
                    <span style={{ fontSize: 11, color: '#AAA' }}>orig: {datFmt(order.leverdatum_origineel)}</span>
                  </>
                )}
                {isPlanner && (
                  <button onClick={() => { setLeverdatumEdit(true); setLeverdatumNieuw(order.leverdatum ? order.leverdatum.substring(0,10) : ''); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', fontSize: 13, padding: '0 2px' }} title="Leverdatum aanpassen">✏️</button>
                )}
              </div>
            )}
          </div>

          <InfoVak label="Tekeningnummer">{order.tekeningnummer}</InfoVak>
          {(order.diameter_mm || order.tanden_per_stuk) && (
            <>
              <InfoVak label="Diameter">
                {order.diameter_mm ? `${parseFloat(order.diameter_mm)} mm` : '—'}
              </InfoVak>
              <InfoVak label="Tanden per stuk">
                {order.tanden_per_stuk
                  ? `${order.tanden_per_stuk} t/stuk × ${order.aantal} = ${order.tanden_per_stuk * order.aantal} tanden`
                  : '—'}
              </InfoVak>
            </>
          )}
        </div>

        {(order.uitbesteding_info || order.opmerkingen) && (
          <div style={{ borderTop: '1px solid #F0F0F0', paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {order.uitbesteding_info && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                <span style={{ fontSize: 11, color: '#888', minWidth: 100 }}>Uitbesteding</span>
                <span style={{ fontSize: 13, color: '#555' }}>{order.uitbesteding_info}</span>
              </div>
            )}
            {order.opmerkingen && (
              <div style={{ padding: '8px 12px', background: '#FFFBF0', border: '1px solid #F0E0A0', borderRadius: 6, fontSize: 13, color: '#555' }}>
                {order.opmerkingen}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Hoofdmateriaal ─────────────────────────────────────────────────── */}
      <div style={S.kaart}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: '#1F3864' }}>Hoofdmateriaal</div>
          {isPlanner && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, color: '#888' }}>Status:</span>
              <select value={order.materiaal_status || 'in_stock'} onChange={e => wijzigMateriaalStatus(e.target.value)}
                disabled={materiaalBezig}
                style={{
                  fontSize: 12, fontWeight: 600,
                  color: MAT_STATUS_KL[order.materiaal_status] || '#333',
                  background: MAT_STATUS_BG[order.materiaal_status] || '#fff',
                  border: `1px solid ${MAT_STATUS_KL[order.materiaal_status] || '#DDD'}40`,
                  borderRadius: 6, padding: '4px 8px', cursor: 'pointer',
                }}>
                {Object.entries(MAT_STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          )}
          {!isPlanner && (
            <Chip bg={MAT_STATUS_BG[order.materiaal_status]} cl={MAT_STATUS_KL[order.materiaal_status]}>
              {MAT_STATUS_LABEL[order.materiaal_status] || order.materiaal_status}
            </Chip>
          )}
        </div>

        {heeftHoofdMateriaal ? (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 12, marginBottom: 10 }}>
              <InfoVak label="Omschrijving">{order.materiaal_beschrijving}</InfoVak>
              <InfoVak label="Referentie">{order.materiaal_referentie}</InfoVak>
              <InfoVak label="Leverancier">{order.leverancier_naam}</InfoVak>
            </div>
            {(order.materiaal_leverdatum_verwacht || order.materiaal_besteldatum) && (
              <div style={{ fontSize: 12, color: '#666', background: '#F9F9F9', border: '1px solid #EEE', borderRadius: 6, padding: '8px 12px', display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                {order.materiaal_besteldatum && (
                  <span>Besteld op: <strong title={volledigeDatum(order.materiaal_besteldatum)}>{weekMetDatum(order.materiaal_besteldatum)}</strong></span>
                )}
                {order.materiaal_leverdatum_verwacht && (() => {
                  const herzien = order.materiaal_leverdatum_verwacht_herzien;
                  const actief = herzien || order.materiaal_leverdatum_verwacht;
                  const vandaag = new Date(); vandaag.setHours(0,0,0,0);
                  const isLaat = new Date(actief) < vandaag && order.materiaal_status !== 'geleverd';
                  return (
                    <span style={{ color: isLaat ? '#C0392B' : 'inherit' }}>
                      {isLaat ? '⚠ ' : ''}Verwachte levering materiaal:{' '}
                      <strong>{new Date(actief).toLocaleDateString('nl-BE')}</strong>
                      {herzien && (
                        <>
                          <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 99, background: '#FFF2CC', color: '#7F6000', border: '1px solid #F0C060', fontWeight: 600, fontSize: 10 }}>herzien</span>
                          <span style={{ color: '#BBB', marginLeft: 6 }} title={volledigeDatum(order.materiaal_leverdatum_verwacht)}>orig: {weekMetDatum(order.materiaal_leverdatum_verwacht)}</span>
                        </>
                      )}
                    </span>
                  );
                })()}
              </div>
            )}
          </>
        ) : (
          <div style={{ fontSize: 13, color: '#AAA', fontStyle: 'italic' }}>Geen hoofdmateriaal ingevuld.</div>
        )}
      </div>

      {/* ── Extra materialen ───────────────────────────────────────────────── */}
      <div style={S.kaart}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: '#1F3864' }}>
            Extra materialen
            {order.materialen?.length > 0 && (
              <span style={{ fontSize: 12, color: '#888', fontWeight: 400, marginLeft: 8 }}>{order.materialen.length} item(s)</span>
            )}
          </div>
          {isPlanner && !nieuwMateriaal && (
            <button style={S.btnBlue} onClick={() => setNieuwMateriaal({ ...leegMateriaal })}>+ Toevoegen</button>
          )}
        </div>

        {nieuwMateriaal && (
          <MateriaalForm form={nieuwMateriaal} setForm={setNieuwMateriaal}
            routeOperaties={routeOperaties} leveranciers={leveranciers}
            orderAantal={order.aantal} onSla={voegMateriaalToe}
            onAnnuleer={() => setNieuwMateriaal(null)} bezig={matBezig} />
        )}

        {order.materialen?.length === 0 && !nieuwMateriaal && (
          <div style={{ fontSize: 13, color: '#AAA', fontStyle: 'italic' }}>Geen extra materialen gekoppeld.</div>
        )}

        {order.materialen?.map(mat => (
          <MateriaalRij key={mat.id} mat={mat}
            routeOperaties={routeOperaties} leveranciers={leveranciers}
            orderAantal={order.aantal} isPlanner={isPlanner} onVervers={laadOrder} />
        ))}
      </div>

      {/* ── Routekaart ─────────────────────────────────────────────────────── */}
      <div style={S.kaart}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: '#1F3864' }}>Routekaart</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {volgordeEdit ? (
              <>
                <span style={{ fontSize: 12, color: '#854F0B', fontWeight: 500 }}>Volgorde aanpassen</span>
                <button onClick={slaVolgordeOp} disabled={volgordeBezig}
                  style={{ padding: '4px 12px', background: '#1A7A3C', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
                  {volgordeBezig ? 'Opslaan...' : '✓ Opslaan'}
                </button>
                <button onClick={() => setVolgordeEdit(null)}
                  style={{ padding: '4px 10px', background: 'none', border: '1px solid #DDD', borderRadius: 6, fontSize: 12, cursor: 'pointer', color: '#555' }}>
                  Annuleren
                </button>
              </>
            ) : (
              <>
                {isPlanner && order.route?.length > 1 && (
                  <button onClick={startVolgordeEdit}
                    style={{ padding: '3px 10px', background: 'none', border: '1px solid #CCC', borderRadius: 6, fontSize: 11, cursor: 'pointer', color: '#666' }}>
                    ↕ Volgorde aanpassen
                  </button>
                )}
                {(() => {
                  if (!order.route?.length) return null;
                  let totOp = 0, totMac = 0, ontbreekt = false;
                  for (const stap of order.route) {
                    if (stap.tijdsdriver === 'order_specifiek') {
                      if (stap.tijd_min) totOp += parseFloat(stap.tijd_min);
                      else ontbreekt = true;
                    } else {
                      const t = berekenStapTijd(stap);
                      if (t) { totOp += t.op; totMac += t.mac; }
                    }
                  }
                  if (totOp === 0 && totMac === 0) return null;
                  return (
                    <span style={{ fontSize: 11, color: '#267A5C', background: '#EEF4EE', border: '0.5px solid #90C0A0', borderRadius: 99, padding: '2px 9px' }}>
                      👤 {fmtMin(totOp)}{totMac > 0 ? ` · 🔧 ${fmtMin(totMac)}` : ''}{ontbreekt ? ' ⚠' : ''}
                    </span>
                  );
                })()}
                <span style={{ fontSize: 13, color: '#888' }}>{voltooideStappen}/{totaalStappen} stappen — {voortgang}%</span>
              </>
            )}
          </div>
        </div>
        <div style={{ height: 5, background: '#EEE', borderRadius: 99, marginBottom: 16 }}>
          <div style={{ width: `${voortgang}%`, height: 5, background: voortgang === 100 ? '#639922' : '#185FA5', borderRadius: 99, transition: 'width 0.3s' }} />
        </div>

        {order.route?.length === 0 && (
          <div style={{ fontSize: 13, color: '#AAA', fontStyle: 'italic' }}>Geen routestappen gedefinieerd.</div>
        )}

        {/* ── Volgorde-bewerkmodus ── */}
        {volgordeEdit && (
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 12, color: '#854F0B', background: '#FFF8EE', border: '1px solid #F0D090', borderRadius: 6, padding: '8px 12px', marginBottom: 10 }}>
              Gebruik de pijlknoppen om stappen te verplaatsen. Klik <strong>Opslaan</strong> om de nieuwe volgorde op te slaan.
            </div>
            {volgordeEdit.map((stap, i) => (
              <div key={stap.id} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', marginBottom: 5,
                background: STAP_BG[stap.status], border: '1px solid #E0E0E0', borderRadius: 8,
              }}>
                <div style={{ minWidth: 20, textAlign: 'center', fontSize: 11, color: '#AAA', fontWeight: 600 }}>{i + 1}</div>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: 13, fontWeight: 500, color: STAP_CL[stap.status] }}>{stap.label || stap.operatie_naam}</span>
                  {stap.is_extern && stap.externe_partij && (
                    <span style={{ fontSize: 11, color: '#888', marginLeft: 8 }}>↳ {stap.externe_partij}</span>
                  )}
                  <span style={{ marginLeft: 8, fontSize: 10, padding: '1px 7px', borderRadius: 99, background: STAP_BG[stap.status], color: STAP_CL[stap.status], border: '0.5px solid currentColor', fontWeight: 600 }}>
                    {STAP_LABEL[stap.status]}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <button onClick={() => verschuifStap(i, -1)} disabled={i === 0}
                    style={{ padding: '2px 8px', background: i === 0 ? '#F5F5F5' : '#E6F1FB', border: '1px solid #C5D8F0', borderRadius: 4, cursor: i === 0 ? 'default' : 'pointer', fontSize: 12, color: i === 0 ? '#CCC' : '#185FA5', lineHeight: 1 }}>
                    ▲
                  </button>
                  <button onClick={() => verschuifStap(i, 1)} disabled={i === volgordeEdit.length - 1}
                    style={{ padding: '2px 8px', background: i === volgordeEdit.length - 1 ? '#F5F5F5' : '#E6F1FB', border: '1px solid #C5D8F0', borderRadius: 4, cursor: i === volgordeEdit.length - 1 ? 'default' : 'pointer', fontSize: 12, color: i === volgordeEdit.length - 1 ? '#CCC' : '#185FA5', lineHeight: 1 }}>
                    ▼
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {!volgordeEdit && order.route?.map((stap, i) => (
          <div key={stap.id} style={{
            display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 14px', marginBottom: 6,
            background: STAP_BG[stap.status], borderRadius: 8,
            border: stap.status === 'actief' ? '1.5px solid #185FA5' : '1px solid #E8E8E8',
          }}>
            <div style={{ minWidth: 22, textAlign: 'center', fontSize: 11, color: '#AAA', paddingTop: 3, fontWeight: 600 }}>{i + 1}</div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 2 }}>
                <span style={{ fontSize: 13, fontWeight: 500, color: STAP_CL[stap.status] }}>
                  {stap.label || stap.operatie_naam}
                </span>
                {stap.label && stap.label !== stap.operatie_naam && (
                  <span style={{ fontSize: 11, color: '#AAA' }}>{stap.operatie_naam}</span>
                )}
                {stap.is_extern && (
                  <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 99, background: '#FAEEDA', color: '#633806', fontWeight: 600 }}>extern</span>
                )}
                <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 99, background: STAP_BG[stap.status], color: STAP_CL[stap.status], border: '0.5px solid currentColor', fontWeight: 600 }}>
                  {STAP_LABEL[stap.status]}
                </span>
                {/* Tijdsweergave */}
                {(() => {
                  if (stap.tijdsdriver === 'order_specifiek') {
                    if (stap.tijd_min) return (
                      <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 99, background: '#F0F0F8', color: '#4A4A8A', border: '0.5px solid #B0B0D0', fontWeight: 500 }}>
                        🕐 {fmtMin(parseFloat(stap.tijd_min))}
                      </span>
                    );
                    return <span style={{ fontSize: 10, color: '#C0A060', fontStyle: 'italic' }}>⏱ tijd niet ingevuld</span>;
                  }
                  const t = berekenStapTijd(stap);
                  if (!t) return null;
                  return (
                    <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 99, background: '#EEF4EE', color: '#267A5C', border: '0.5px solid #90C0A0', fontWeight: 500 }}>
                      👤 {fmtMin(t.op)}{t.mac > 0 ? ` · 🔧 ${fmtMin(t.mac)}` : ''}
                    </span>
                  );
                })()}
              </div>

              {stap.is_extern && stap.externe_partij && (
                <div style={{ fontSize: 12, color: '#633806', marginBottom: 4 }}>↳ {stap.externe_partij}</div>
              )}

              {/* Instructies voor operator */}
              {instructiesEdit?.stapId === stap.id ? (
                <div style={{ marginTop: 6, marginBottom: 4 }}>
                  <textarea rows={3} value={instructiesEdit.tekst}
                    onChange={e => setInstructiesEdit(v => ({ ...v, tekst: e.target.value }))}
                    placeholder="Instructies voor de operator..."
                    style={{ width: '100%', padding: '6px 10px', border: '1px solid #F0C060', borderRadius: 6, fontSize: 12, boxSizing: 'border-box', resize: 'vertical' }} />
                  <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                    <button onClick={slaInstructiesOp}
                      style={{ padding: '4px 12px', background: '#1F3864', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 500 }}>
                      Opslaan
                    </button>
                    <button onClick={() => setInstructiesEdit(null)}
                      style={{ padding: '4px 10px', background: 'none', border: '1px solid #DDD', borderRadius: 6, fontSize: 12, cursor: 'pointer', color: '#555' }}>
                      Annuleren
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {stap.instructies && (
                    <div style={{ marginTop: 4, marginBottom: 4, padding: '6px 10px', background: '#FFFBEB', border: '1px solid #F0C060', borderRadius: 6, fontSize: 12, color: '#5A3E00' }}>
                      <strong>📋 Instructies:</strong> {stap.instructies}
                    </div>
                  )}
                  {isPlanner && stap.status !== 'voltooid' && (
                    <button onClick={() => setInstructiesEdit({ stapId: stap.id, tekst: stap.instructies || '' })}
                      style={{ marginTop: 4, fontSize: 11, color: '#888', background: 'none', border: '1px dashed #CCC', borderRadius: 5, padding: '2px 8px', cursor: 'pointer' }}>
                      {stap.instructies ? '✏ Instructies bewerken' : '+ Instructies toevoegen'}
                    </button>
                  )}
                </>
              )}

              {/* Handmatige tijdsinvoer voor order_specifiek operaties (planner) */}
              {isPlanner && stap.tijdsdriver === 'order_specifiek' && stap.status !== 'voltooid' && (
                <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, color: '#666' }}>Geschatte tijd:</span>
                  {tijdEdit[stap.id] !== undefined ? (
                    <>
                      <input type="number" min="0" step="1" placeholder="minuten"
                        value={tijdEdit[stap.id]}
                        onChange={e => setTijdEdit(t => ({ ...t, [stap.id]: e.target.value }))}
                        style={{ width: 80, padding: '3px 7px', border: '1px solid #B0B0D0', borderRadius: 5, fontSize: 12, textAlign: 'right' }} />
                      <span style={{ fontSize: 11, color: '#888' }}>min</span>
                      <button onClick={() => slaaTijdOp(stap.id)}
                        style={{ padding: '3px 10px', background: '#1F3864', color: '#fff', border: 'none', borderRadius: 5, fontSize: 11, cursor: 'pointer' }}>
                        Sla op
                      </button>
                      <button onClick={() => setTijdEdit(t => { const n = { ...t }; delete n[stap.id]; return n; })}
                        style={{ padding: '3px 8px', background: 'none', border: '1px solid #DDD', borderRadius: 5, fontSize: 11, cursor: 'pointer', color: '#666' }}>
                        ✕
                      </button>
                    </>
                  ) : (
                    <button onClick={() => setTijdEdit(t => ({ ...t, [stap.id]: stap.tijd_min != null ? String(stap.tijd_min) : '' }))}
                      style={{ fontSize: 11, color: stap.tijd_min ? '#4A4A8A' : '#B08030', background: 'none', border: `1px ${stap.tijd_min ? 'solid #B0B0D0' : 'dashed #C0A060'}`, borderRadius: 5, padding: '2px 8px', cursor: 'pointer' }}>
                      {stap.tijd_min ? `⏱ ${fmtMin(parseFloat(stap.tijd_min))} — bewerken` : '+ Tijd invoeren'}
                    </button>
                  )}
                </div>
              )}

              {stap.opmerking && (
                <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>Opmerking operator: {stap.opmerking}</div>
              )}
              {stap.materiaal_naam && (
                <div style={{ fontSize: 12, color: '#267A5C', background: '#EBF8F2', border: '1px solid #82C96B', borderRadius: 5, padding: '3px 10px', display: 'inline-block', marginTop: 4 }}>
                  📦 {stap.materiaal_naam}
                  {stap.materiaal_referentie && <span style={{ color: '#555', marginLeft: 6 }}>{stap.materiaal_referentie}</span>}
                  {stap.materiaal_hoeveelheid && <span style={{ marginLeft: 6 }}>· {parseFloat(stap.materiaal_hoeveelheid)} {stap.materiaal_eenheid || 'stuks'}</span>}
                </div>
              )}
              {stap.voltooid_op && (
                <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                  Voltooid op {datFmt(stap.voltooid_op)} door {stap.voltooid_door_naam}
                </div>
              )}

              {stap.status === 'actief' && (
                <div style={{ marginTop: 8, display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 6 }}>
                  <input placeholder="Opmerking (optioneel)" value={stapOpmerking[stap.id] || ''}
                    onChange={e => setStapOpmerking(p => ({ ...p, [stap.id]: e.target.value }))}
                    style={{ flex: 1, padding: '8px 10px', border: '1px solid #DDD', borderRadius: 6, fontSize: 13 }} />
                  <button onClick={() => vinkAf(stap.id)} disabled={bezig === stap.id}
                    style={{ padding: isMobile ? '11px 16px' : '5px 16px', background: '#1A7A3C', color: '#fff', border: 'none', borderRadius: 6, fontSize: isMobile ? 14 : 12, cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {bezig === stap.id ? 'Bezig...' : 'Voltooid ✓'}
                  </button>
                </div>
              )}
            </div>

            {isPlanner && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end', flexShrink: 0 }}>
                {stap.status === 'voltooid' && (
                  <button onClick={() => zetTerug(stap.id)} disabled={bezig === stap.id}
                    style={{ fontSize: 11, padding: '4px 10px', background: 'none', border: '1px solid #B5660E', borderRadius: 6, color: '#B5660E', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    Terugzetten
                  </button>
                )}
                {stap.status !== 'voltooid' && (
                  <button onClick={() => verwijderStap(stap.id, stap.label || stap.operatie_naam)}
                    title="Stap verwijderen"
                    style={{ fontSize: 11, padding: '4px 8px', background: 'none', border: '1px solid #E0A0A0', borderRadius: 6, color: '#A32D2D', cursor: 'pointer', whiteSpace: 'nowrap', opacity: 0.6 }}>
                    🗑
                  </button>
                )}
              </div>
            )}
          </div>
        ))}

        {/* ── Stap toevoegen (planner) ── */}
        {isPlanner && !volgordeEdit && (
          <div style={{ marginTop: 10 }}>
            {nieuwStap ? (
              <div style={{ background: '#F7F9FC', border: '1px dashed #185FA5', borderRadius: 8, padding: '12px 14px' }}>
                <div style={{ fontWeight: 500, fontSize: 13, color: '#1F3864', marginBottom: 10 }}>Nieuwe stap toevoegen</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                  <select
                    value={nieuwStap.operatie_id || ''}
                    onChange={e => setNieuwStap(s => ({ ...s, operatie_id: e.target.value }))}
                    style={{ flex: 1, minWidth: 160, padding: '6px 10px', border: '1px solid #DDD', borderRadius: 6, fontSize: 13 }}>
                    <option value="">— Kies operatie —</option>
                    {operaties.map(o => <option key={o.id} value={o.id}>{o.nummer}. {o.naam}</option>)}
                  </select>
                  <input
                    placeholder="Label (optioneel)"
                    value={nieuwStap.label || ''}
                    onChange={e => setNieuwStap(s => ({ ...s, label: e.target.value }))}
                    style={{ flex: 1, minWidth: 120, padding: '6px 10px', border: '1px solid #DDD', borderRadius: 6, fontSize: 13 }} />
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#555', marginBottom: nieuwStap.is_extern ? 8 : 0, cursor: 'pointer' }}>
                  <input type="checkbox" checked={nieuwStap.is_extern || false}
                    onChange={e => setNieuwStap(s => ({ ...s, is_extern: e.target.checked }))} />
                  Extern (uitbesteed)
                </label>
                {nieuwStap.is_extern && (
                  <input
                    placeholder="Externe partij"
                    value={nieuwStap.externe_partij || ''}
                    onChange={e => setNieuwStap(s => ({ ...s, externe_partij: e.target.value }))}
                    style={{ width: '100%', padding: '6px 10px', border: '1px solid #DDD', borderRadius: 6, fontSize: 13, marginTop: 4, boxSizing: 'border-box' }} />
                )}
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <button onClick={voegStapToe} disabled={stapBezig || !nieuwStap.operatie_id}
                    style={{ padding: '6px 14px', background: '#185FA5', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 500, opacity: !nieuwStap.operatie_id ? 0.5 : 1 }}>
                    {stapBezig ? 'Toevoegen...' : 'Toevoegen'}
                  </button>
                  <button onClick={() => setNieuwStap(null)}
                    style={{ padding: '6px 12px', background: 'none', border: '1px solid #DDD', borderRadius: 6, fontSize: 13, cursor: 'pointer', color: '#555' }}>
                    Annuleren
                  </button>
                </div>
              </div>
            ) : (
              <button onClick={() => setNieuwStap({ operatie_id: '', label: '', is_extern: false, externe_partij: '' })}
                style={{ fontSize: 12, color: '#185FA5', background: 'none', border: '1px dashed #185FA5', borderRadius: 6, padding: '5px 14px', cursor: 'pointer' }}>
                + Stap toevoegen
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Historiek tijdlijn ─────────────────────────────────────────────── */}
      {order.historiek?.length > 0 && (
        <div style={S.kaart}>
          <div style={{ fontWeight: 600, fontSize: 14, color: '#1F3864', marginBottom: 16 }}>
            Activiteitslog
            <span style={{ fontSize: 12, fontWeight: 400, color: '#888', marginLeft: 8 }}>{order.historiek.length} gebeurtenissen</span>
          </div>
          <div style={{ position: 'relative' }}>
            {/* Verticale lijn */}
            {!isMobile && (
              <div style={{ position: 'absolute', left: 17, top: 8, bottom: 8, width: 2, background: '#EEE', borderRadius: 1 }} />
            )}
            {order.historiek.map((h, idx) => {
              const cfg = ACTIE_CFG[h.actie] || { icon: '•', kleur: '#888', label: h.actie.replace(/_/g, ' ').toLowerCase() };
              const dt = new Date(h.tijdstip);
              const datumStr = dt.toLocaleDateString('nl-BE', { day: '2-digit', month: '2-digit', year: '2-digit' });
              const tijdStr = dt.toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' });

              // Bouw detail-tekst op basis van actie + details
              const d = h.details || {};
              let detail = null;

              if (h.actie === 'STAP_VOLTOOID') {
                detail = (
                  <div>
                    <span style={{ fontWeight: 600, color: '#1F3864' }}>{d.stap_naam || `Stap ${d.volgorde}`}</span>
                    {d.opmerking && <span style={{ color: '#666', marginLeft: 8 }}>— "{d.opmerking}"</span>}
                  </div>
                );
              } else if (h.actie === 'STAP_TERUGGEZET') {
                detail = <span style={{ fontWeight: 600, color: '#1F3864' }}>{d.stap_naam || `Stap ${d.volgorde}`}</span>;
              } else if (h.actie === 'INSTRUCTIES_GEWIJZIGD') {
                detail = (
                  <div>
                    <span style={{ fontWeight: 500 }}>{d.stap_naam}</span>
                    {d.instructies && <div style={{ fontSize: 11, color: '#666', marginTop: 2, padding: '4px 8px', background: '#FFFBEB', border: '1px solid #F0C060', borderRadius: 4 }}>"{d.instructies}"</div>}
                    {!d.instructies && <span style={{ color: '#AAA', marginLeft: 8 }}>— instructies gewist</span>}
                  </div>
                );
              } else if (h.actie === 'LEVERDATUM_GEWIJZIGD') {
                detail = (
                  <span>
                    <span style={{ color: '#AAA', textDecoration: 'line-through', marginRight: 6 }}>{datFmt(d.oude_datum)}</span>
                    <span style={{ fontWeight: 700, color: '#1F3864' }}>{datFmt(d.nieuwe_datum)}</span>
                  </span>
                );
              } else if (h.actie === 'ORDER_BIJGEWERKT' && d.wijzigingen?.length > 0) {
                const fmt = (veld, val) => {
                  if (veld === 'Leverdatum') return val ? datFmt(val) : '—';
                  if (veld === 'Status') return STATUS_NL[val] || val;
                  if (veld === 'Materiaalstatus') return MAT_NL2[val] || val;
                  if (veld === 'Prioriteit') return PRIO_NL[val] || val;
                  return val == null || val === '' ? '—' : String(val);
                };
                detail = (
                  <div>
                    {d.wijzigingen.map((w, i) => (
                      <div key={i} style={{ fontSize: 12, marginBottom: 2 }}>
                        <span style={{ color: '#888', minWidth: 100, display: 'inline-block' }}>{w.veld}:</span>
                        <span style={{ color: '#AAA', textDecoration: 'line-through', marginRight: 6 }}>{fmt(w.veld, w.oud)}</span>
                        <span style={{ fontWeight: 600, color: '#1F3864' }}>{fmt(w.veld, w.nieuw)}</span>
                      </div>
                    ))}
                  </div>
                );
              } else if ((h.actie === 'MATERIAALSTATUS_GEWIJZIGD' || h.actie === 'MATERIAAL_STATUS_GEWIJZIGD') && d.materiaal) {
                detail = (
                  <span>
                    <span style={{ fontWeight: 500 }}>{d.materiaal}</span>
                    {d.nieuwe_status && <span style={{ marginLeft: 8, padding: '1px 7px', borderRadius: 99, fontSize: 11, background: MAT_STATUS_BG[d.nieuwe_status], color: MAT_STATUS_KL[d.nieuwe_status] }}>{MAT_NL2[d.nieuwe_status]}</span>}
                  </span>
                );
              } else if (h.actie === 'MATERIAAL_TOEGEVOEGD' && d.materiaal) {
                detail = <span style={{ fontWeight: 500 }}>{d.materiaal}</span>;
              } else if (h.actie === 'STATUS_GEWIJZIGD' && d.nieuwe_status) {
                detail = <span style={{ padding: '1px 8px', borderRadius: 99, fontSize: 11, background: STATUS_CHIP[d.nieuwe_status]?.bg || '#F0F0F0', color: STATUS_CHIP[d.nieuwe_status]?.cl || '#555', fontWeight: 600 }}>{STATUS_NL[d.nieuwe_status] || d.nieuwe_status}</span>;
              } else if (h.actie === 'ORDER_GEDUPLICEERD') {
                detail = <span style={{ color: '#888' }}>kopie van {d.origineel} → nieuw: <strong>{d.nieuw}</strong></span>;
              } else if (h.actie === 'MATERIAALLEVERDATUM_GEWIJZIGD' && d.leverdatum) {
                detail = <span style={{ fontWeight: 600, color: '#1F3864' }}>{datFmt(d.leverdatum)}</span>;
              }

              return (
                <div key={h.id} style={{ display: 'flex', gap: isMobile ? 10 : 14, marginBottom: idx === order.historiek.length - 1 ? 0 : 10, position: 'relative' }}>
                  {/* Icoon */}
                  <div style={{
                    width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                    background: cfg.kleur + '18', border: `1.5px solid ${cfg.kleur}40`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 15, zIndex: 1,
                  }}>
                    {cfg.icon}
                  </div>

                  {/* Inhoud */}
                  <div style={{ flex: 1, paddingTop: 5 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginBottom: detail ? 4 : 0 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: cfg.kleur }}>{cfg.label}</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#333' }}>{h.gebruiker_naam}</span>
                      <span style={{ fontSize: 11, color: '#AAA', marginLeft: 'auto' }}>{datumStr} {tijdStr}</span>
                    </div>
                    {detail && <div style={{ fontSize: 12, color: '#444', lineHeight: 1.5 }}>{detail}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Bewerk modal ───────────────────────────────────────────────────── */}
      {bewerkForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '40px 16px' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: '100%', maxWidth: 700, boxShadow: '0 8px 32px rgba(0,0,0,0.2)', marginBottom: 40 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ margin: 0, fontSize: 17, color: '#1F3864' }}>Order bewerken — {order.ordernummer}</h2>
              <button style={S.btnGhost} onClick={() => setBewerkForm(null)}>✕</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ fontSize: 11, color: '#666' }}>Ordernummer</label>
                <input style={S.input} value={bewerkForm.ordernummer} onChange={e => setBewerkForm(f => ({ ...f, ordernummer: e.target.value }))} placeholder="bijv. ORD-2025-042" />
              </div>
              <div>
                <label style={{ fontSize: 11, color: '#666' }}>Klant *</label>
                <input style={S.input} value={bewerkForm.klant} onChange={e => setBewerkForm(f => ({ ...f, klant: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 11, color: '#666' }}>Klant referentie</label>
                <input style={S.input} value={bewerkForm.klant_referentie} onChange={e => setBewerkForm(f => ({ ...f, klant_referentie: e.target.value }))} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ fontSize: 11, color: '#666' }}>Artikel omschrijving *</label>
                <input style={S.input} value={bewerkForm.artikel_omschrijving} onChange={e => setBewerkForm(f => ({ ...f, artikel_omschrijving: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 11, color: '#666' }}>Aantal *</label>
                <input style={S.input} type="number" min="1" value={bewerkForm.aantal} onChange={e => setBewerkForm(f => ({ ...f, aantal: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 11, color: '#666' }}>Tekeningnummer</label>
                <input style={S.input} value={bewerkForm.tekeningnummer} onChange={e => setBewerkForm(f => ({ ...f, tekeningnummer: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 11, color: '#666' }}>Diameter (mm)</label>
                <input style={S.input} type="number" min="0" step="0.1" placeholder="bijv. 400" value={bewerkForm.diameter_mm} onChange={e => setBewerkForm(f => ({ ...f, diameter_mm: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 11, color: '#666' }}>Tanden per stuk</label>
                <input style={S.input} type="number" min="0" step="1" placeholder="bijv. 60" value={bewerkForm.tanden_per_stuk} onChange={e => setBewerkForm(f => ({ ...f, tanden_per_stuk: e.target.value }))} />
              </div>
              <WeekDatumPicker
                label="Startdatum"
                value={bewerkForm.startdatum}
                onChange={v => setBewerkForm(f => ({ ...f, startdatum: v }))}
              />
              <WeekDatumPicker
                label="Leverdatum *"
                value={bewerkForm.leverdatum}
                onChange={v => setBewerkForm(f => ({ ...f, leverdatum: v }))}
                eindDatum
              />
              <div>
                <label style={{ fontSize: 11, color: '#666' }}>Prioriteit</label>
                <select style={S.input} value={bewerkForm.prioriteit} onChange={e => setBewerkForm(f => ({ ...f, prioriteit: e.target.value }))}>
                  <option value="normaal">Normaal</option>
                  <option value="hoog">Hoog</option>
                  <option value="spoed">Spoed</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 11, color: '#666' }}>Status</label>
                <select style={S.input} value={bewerkForm.status} onChange={e => setBewerkForm(f => ({ ...f, status: e.target.value }))}>
                  <option value="gepland">Gepland</option>
                  <option value="in_uitvoering">In uitvoering</option>
                  <option value="voltooid">Voltooid</option>
                  <option value="on_hold">On hold</option>
                  <option value="geannuleerd">Geannuleerd</option>
                </select>
              </div>
            </div>

            <div style={{ borderTop: '1px solid #EEE', margin: '18px 0', paddingTop: 18 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 12 }}>Hoofdmateriaal</div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ fontSize: 11, color: '#666' }}>Omschrijving</label>
                  <input style={S.input} value={bewerkForm.materiaal_beschrijving} onChange={e => setBewerkForm(f => ({ ...f, materiaal_beschrijving: e.target.value }))} />
                </div>
                <div>
                  <label style={{ fontSize: 11, color: '#666' }}>Referentie</label>
                  <input style={S.input} value={bewerkForm.materiaal_referentie} onChange={e => setBewerkForm(f => ({ ...f, materiaal_referentie: e.target.value }))} />
                </div>
                <div>
                  <label style={{ fontSize: 11, color: '#666' }}>Leverancier</label>
                  <select style={S.input} value={bewerkForm.materiaal_leverancier_id} onChange={e => setBewerkForm(f => ({ ...f, materiaal_leverancier_id: e.target.value }))}>
                    <option value="">— geen —</option>
                    {leveranciers.map(l => <option key={l.id} value={l.id}>{l.naam}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 11, color: '#666' }}>Materiaalstatus</label>
                  <select style={S.input} value={bewerkForm.materiaal_status} onChange={e => setBewerkForm(f => ({ ...f, materiaal_status: e.target.value }))}>
                    {Object.entries(MAT_STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 11, color: '#666' }}>Uitbesteding info</label>
              <input style={S.input} value={bewerkForm.uitbesteding_info} onChange={e => setBewerkForm(f => ({ ...f, uitbesteding_info: e.target.value }))} placeholder="Externe partij, details..." />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 11, color: '#666' }}>Opmerkingen</label>
              <textarea style={{ ...S.input, minHeight: 70, resize: 'vertical' }} value={bewerkForm.opmerkingen} onChange={e => setBewerkForm(f => ({ ...f, opmerkingen: e.target.value }))} />
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button style={S.btnGreen} onClick={slaOrderOp} disabled={bewerkBezig || !bewerkForm.klant || !bewerkForm.artikel_omschrijving}>
                {bewerkBezig ? 'Opslaan...' : 'Opslaan'}
              </button>
              <button style={S.btnGhost} onClick={() => setBewerkForm(null)}>Annuleren</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
